import React, { useState } from 'react';
import { CalendarXIcon, RotateCcwIcon, Trash2Icon, TrashIcon } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { useJournal } from '../context/JournalContext';
import { useDisplayMode } from '../context/DisplayModeContext';
import { daysLeft, TRASH_RETENTION_DAYS } from '../lib/trash';
import { Button } from '../components/ui/Button';
import { cn } from '../lib/cn';
import { fmtMoneyEs, fmtPctEs } from '../lib/format';
export function TrashPage() {
  const {
    state,
    trashedOperations,
    trashedJournals,
    restoreOperation,
    destroyOperation,
    restoreDay,
    destroyDay,
    emptyTrash,
    categoriesSorted
  } = useJournal();
  const {
    mode,
    currency
  } = useDisplayMode();
  const [confirmEmpty, setConfirmEmpty] = useState(false);
  const tagLabels = (operationTagIds: string[], category: {
    id: string;
  } | undefined) => {
    if (!category) return '—';
    const labels = state.tags.filter(tag => tag.categoryId === category.id && operationTagIds.includes(tag.id)).map(tag => tag.label);
    return labels.length ? labels.join(', ') : '—';
  };
  const columns = categoriesSorted.slice(0, 3);
  const isEmpty = trashedOperations.length === 0 && trashedJournals.length === 0;
  return <div className="mx-auto max-w-6xl" data-id="element-841" data-mp="3o3lioxxa6ttt">
      <header className="mb-5 flex flex-wrap items-start justify-between gap-3" data-id="element-842" data-mp="3o4ms6xynf89f">
        <div data-id="element-843" data-mp="30fkbbsbu9jfz">
          <h1 className="text-xl font-semibold tracking-tight" data-id="element-844" data-mp="30f27oamuk2y1">Papelera</h1>
          <p className="mt-0.5 text-[13px] leading-relaxed text-ink-soft" data-id="element-845" data-mp="2vgh88dff5nan">
            Las operaciones y los días eliminados se conservan {TRASH_RETENTION_DAYS} días con sus notas, imágenes y
            etiquetas. No cuentan en ningún cálculo.
          </p>
        </div>
        {!isEmpty && <Button variant="danger" size="sm" onClick={() => setConfirmEmpty(true)} data-id="element-846" data-mp="2hn9jctiq35wn">
            <TrashIcon className="h-3.5 w-3.5" data-id="element-847" data-mp="2cpgd60hf6bwd" />Vaciar papelera
          </Button>}
      </header>

      {isEmpty ? <div className="rounded-2xl border border-dashed border-line bg-surface/40 px-5 py-12 text-center" data-id="element-848" data-mp="gl0gegl7uft4">
          <p className="text-sm font-medium" data-id="element-849" data-mp="ljbq8rbigqbc">La papelera está vacía</p>
          <p className="mx-auto mt-1 max-w-sm text-[13px] text-ink-soft" data-id="element-850" data-mp="qbd0k7p4c6yq">
            Cuando elimines una operación o un día completo aparecerá aquí y podrás restaurarlo durante {TRASH_RETENTION_DAYS} días.
          </p>
        </div> : <div className="space-y-6" data-id="element-851" data-mp="3tueoa4mmxn0e">
          {trashedJournals.length > 0 && <section data-id="element-852" data-mp="2mka2qr9mrbi">
              <h2 className="mb-2 text-[15px] font-semibold tracking-tight" data-id="element-853" data-mp="2o4x7vb937zc">Días eliminados</h2>
              <div className="space-y-2" data-id="element-854" data-mp="6pqfxxagughg">
                {trashedJournals.map(journal => {
            const remaining = daysLeft(journal.deletedAt ?? 0);
            const ops = state.operations.filter(op => op.trashedWithDay === journal.date);
            const images = journal.blocks.filter(block => block.type === 'image').length;
            return <div key={journal.date} className="flex flex-wrap items-center gap-3 rounded-xl border border-line bg-white px-4 py-3" data-id="element-855" data-mp="1x50z81bu1j2r">
                      <CalendarXIcon className="h-4 w-4 shrink-0 text-ink-soft" data-id="element-856" data-mp="1nfr6cj95zwdl" />
                      <div className="min-w-0 flex-1" data-id="element-857" data-mp="1jee2uftjo59u">
                        <p className="truncate text-[13px] font-medium capitalize" data-id="element-858" data-mp="1jdme9oyd77t2">
                          {format(parseISO(journal.date), "EEEE d 'de' MMMM, yyyy", {
                    locale: es
                  })}
                        </p>
                        <p className="mt-0.5 text-[11.5px] text-ink-soft" data-id="element-859" data-mp="19ewwrxadrpdc">
                          {journal.deletedAt && `Eliminado el ${format(new Date(journal.deletedAt), 'dd/MM/yyyy HH:mm')} · `}
                          {ops.length} {ops.length === 1 ? 'operación' : 'operaciones'} · {journal.blocks.length} bloques de notas · {images} {images === 1 ? 'imagen' : 'imágenes'}
                        </p>
                      </div>
                      <span className={cn('shrink-0 text-[12px] tabular-nums', remaining <= 3 ? 'text-neg' : 'text-ink-soft')} data-id="element-860" data-mp="31x0u60q3m968">
                        {remaining === 0 ? 'Se elimina hoy' : `${remaining} ${remaining === 1 ? 'día' : 'días'}`}
                      </span>
                      <div className="flex shrink-0 gap-1" data-id="element-861" data-mp="2sy5pit2pd751">
                        <button onClick={() => restoreDay(journal.date)} className="inline-flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-[12px] text-ink-soft hover:bg-surface hover:text-ink" data-id="element-862" data-mp="2wwmy7h1rupok">
                          <RotateCcwIcon className="h-3.5 w-3.5" data-id="element-863" data-mp="2ny1g1n07pcob" />Restaurar
                        </button>
                        <button onClick={() => destroyDay(journal.date)} className="inline-flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-[12px] text-ink-soft hover:bg-neg-soft hover:text-neg" data-id="element-864" data-mp="299eq6rcyl2iu">
                          <Trash2Icon className="h-3.5 w-3.5" data-id="element-865" data-mp="208qc5wnkf25y" />Eliminar
                        </button>
                      </div>
                    </div>;
          })}
              </div>
            </section>}

          {trashedOperations.length > 0 && <section data-id="element-866" data-mp="1gmndhglcgk3j">
              <h2 className="mb-2 text-[15px] font-semibold tracking-tight" data-id="element-867" data-mp="1gnges21jahea">Operaciones eliminadas</h2>
              <div className="overflow-x-auto rounded-2xl border border-line" data-id="element-868" data-mp="1umpvr3xkdfjb">
                <table className="w-full min-w-[980px] border-collapse text-left" data-id="element-869" data-mp="1qj0588xbwnvn">
                  <thead data-id="element-870" data-mp="24ij40b492mva">
                    <tr className="border-b border-line bg-surface/50 text-[11px] uppercase tracking-wide text-ink-soft" data-id="element-871" data-mp="24hq5tyjcx5or">
                      <Th data-id="element-872" data-mp="1zin9d2hg07se">Operación</Th>
                      <Th data-id="element-873" data-mp="2e9nizatxe7q4">Fecha</Th>
                      <Th data-id="element-874" data-mp="2e8ks32wls5a7">Eliminada</Th>
                      <Th data-id="element-875" data-mp="29ajbqpqkak2f">Resultado</Th>
                      {columns.map(category => <Th key={category.id} data-id="element-876" data-mp="29cdvw1u0e85g">{category.name}</Th>)}
                      <Th data-id="element-877" data-mp="2nb3q99pg1tq4">Restante</Th>
                      <Th align="right" data-id="element-878" data-mp="2n7ze9q5qltow">Acciones</Th>
                    </tr>
                  </thead>
                  <tbody data-id="element-879" data-mp="2i94o235fx2ur">
                    {trashedOperations.map(op => {
                const remaining = daysLeft(op.deletedAt ?? 0);
                const pos = op.resultPct >= 0;
                return <tr key={op.id} className="border-b border-line text-[12.5px] last:border-0" data-id="element-880" data-mp="1oxpximnd2qs4">
                          <Td data-id="element-881" data-mp="1tp7uz0q6e98c">
                            <span className="font-medium" data-id="element-882" data-mp="1tqix6w3z38xc">{op.asset}</span>
                            <span className={cn('ml-2 rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase', op.side === 'buy' ? 'bg-pos-soft text-pos' : 'bg-neg-soft text-neg')} data-id="element-883" data-mp="1ymzmvuqtlvcj">
                              {op.side === 'buy' ? 'Compra' : 'Venta'}
                            </span>
                          </Td>
                          <Td data-id="element-884" data-mp="2cnagv6nffq1x">{format(parseISO(op.date), 'd MMM yyyy', {
                      locale: es
                    })}</Td>
                          <Td data-id="element-885" data-mp="e9irmj69ioxu">{op.deletedAt ? format(new Date(op.deletedAt), 'dd/MM/yyyy HH:mm') : '—'}</Td>
                          <Td data-id="element-886" data-mp="iab21fqvezvp">
                            <span className={cn('font-semibold tabular-nums', pos ? 'text-pos' : 'text-neg')} data-id="element-887" data-mp="aq9y8f0i79hy">
                              {mode === 'money' ? fmtMoneyEs(op.resultMoney ?? 0, true, currency) : fmtPctEs(op.resultPct)}
                            </span>
                          </Td>
                          {columns.map(category => <Td key={category.id} data-id="element-888" data-mp="3t4swmmmqe5f9">{tagLabels(op.tagIds, category)}</Td>)}
                          <Td data-id="element-889" data-mp="tow03w3uh4mx">
                            <span className={cn('tabular-nums', remaining <= 3 ? 'text-neg' : 'text-ink-soft')} data-id="element-890" data-mp="tnuonqix2g4o">
                              {remaining === 0 ? 'Hoy' : `${remaining} ${remaining === 1 ? 'día' : 'días'}`}
                            </span>
                          </Td>
                          <Td align="right" data-id="element-891" data-mp="fq66iqg8hn3b">
                            <div className="flex justify-end gap-1" data-id="element-892" data-mp="frh5mcyqi6o3">
                              <button onClick={() => restoreOperation(op.id)} className="inline-flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-[12px] text-ink-soft hover:bg-surface hover:text-ink" data-id="element-893" data-mp="35fgfn0l5wt2e">
                                <RotateCcwIcon className="h-3.5 w-3.5" data-id="element-894" data-mp="14aw53yffqgcm" />Restaurar
                              </button>
                              <button onClick={() => destroyOperation(op.id)} className="inline-flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-[12px] text-ink-soft hover:bg-neg-soft hover:text-neg" data-id="element-895" data-mp="18by4qgd9kuf4">
                                <Trash2Icon className="h-3.5 w-3.5" data-id="element-896" data-mp="zf7dqidkk5re" />Eliminar
                              </button>
                            </div>
                          </Td>
                        </tr>;
              })}
                  </tbody>
                </table>
              </div>
            </section>}
        </div>}

      {confirmEmpty && <div className="fixed inset-0 z-[80] flex items-center justify-center bg-ink/25 p-5" role="dialog" aria-modal="true" data-id="element-897" data-mp="1ia5n93n7bkj8">
          <div className="w-full max-w-md rounded-2xl border border-line bg-white p-5 shadow-pop" data-id="element-898" data-mp="2as9kklpv9gyd">
            <h2 className="text-[15px] font-semibold tracking-tight" data-id="element-899" data-mp="3violgf7xreva">Vaciar la papelera</h2>
            <p className="mt-2 text-[13px] leading-relaxed text-ink-soft" data-id="element-900" data-mp="3qk3msk8c138y">
              Esta acción eliminará definitivamente todas las operaciones y no podrá deshacerse.
            </p>
            <div className="mt-5 flex justify-end gap-2" data-id="element-901" data-mp="4495sr1nemcy">
              <Button variant="ghost" size="sm" onClick={() => setConfirmEmpty(false)} data-id="element-902" data-mp="44sowb31bgsk">Cancelar</Button>
              <Button variant="danger" size="sm" onClick={() => {
            emptyTrash();
            setConfirmEmpty(false);
          }} data-id="element-903" data-mp="3ckm3ulhpwk7i">
                Eliminar definitivamente
              </Button>
            </div>
          </div>
        </div>}
    </div>;
}
function Th({
  children,
  align = 'left'
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
}) {
  return <th className={cn('px-3 py-2.5 font-medium', align === 'right' && 'text-right')} data-id="element-904" data-mp="10u9rgh6q0mq1">{children}</th>;
}
function Td({
  children,
  align = 'left'
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
}) {
  return <td className={cn('px-3 py-2.5 align-middle', align === 'right' && 'text-right')} data-id="element-905" data-mp="1ybxsxg1e44a4">{children}</td>;
}